import { useState } from 'react';
import { fmtMemberId } from '@lib/member-id';
import { CAME_BACK_WORDING, OPTED_OUT_WORDING, fmtTs, type MemberDetailResp } from '@lib/care-model';

/**
 * MemberDetailPanel - the shared full-record renderer for the reason-gated member reveal
 * (POST /api/care/member-detail). Used by the EventDrawer's "Full record" toggle and by the
 * Lookup page's row expand, so both surfaces read identically.
 *
 * ROW 418 (William, 19 Sep 2026: "make this lookup just like the one for the main portal"): the
 * sections are in the PORTAL'S ORDER and wording (v1 MemberLookup.tsx MemberSection), where Care's
 * detail read carries them, reordered and relabelled rather than rewritten:
 *
 *   the notices (opted out, came back, DO NOT SOLICIT), then
 *   Identity        member id, insurance id, DOB, plan, MSO / IPA, kit stage, last activity;
 *   Member          address, phones, email, language, and the PCP with a "look up this PCP" jump;
 *   Events          the event tail (the Portal's EventTail);
 *   Member results  the lab results and the result letters (the Portal's "Member" section: results
 *                   and the letter tables; Care's read carries result letters only);
 *   Kit tracking    the kits (the Portal's KitTrackingSection; the USPS scans are on the Enroute tab);
 *   PCP communication  the faxes (the Portal's PcpCommunicationSection; Care's read carries the fax
 *                   stream, not the PCP letters, which the PCP tab's expand shows per PCP).
 *
 * NOT CARRIED BY CARE'S READ, and therefore not rendered rather than rendered empty: the Portal's
 * Texts (the SMS timeline lives in the queue's Conversation section here), Releases and catch-up,
 * the welcome and reminder letter tables, and Calls (outbound and Member Care calls). Named here so
 * the absence is a decision and not an oversight.
 *
 * Each satellite renders as a collapsed section with its count; satellites are fail-soft server-side
 * (an older campaign DB returns []), so an empty section just reads "none"; a program that does not
 * offer one says "not part of this program".
 */

function s(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

/** A timestamp-ish satellite cell renders via fmtTs when parseable, else raw. */
function cellVal(v: unknown, isTs: boolean): string {
  const str = s(v);
  if (!isTs || !str) return str;
  const t = Date.parse(str);
  return Number.isFinite(t) ? fmtTs(str) : str;
}

interface ColDef {
  key: string;
  label: string;
  ts?: boolean;
}

function SatelliteSection({
  title,
  rows,
  cols,
  notOffered = false,
}: {
  title: string;
  rows: Record<string, unknown>[];
  cols: ColDef[];
  /** Capability gating, registry half (2026-09-06): the program does not have this at all. */
  notOffered?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (notOffered) {
    return (
      <div style={{ marginTop: 8 }} className="muted small">
        {title}: not part of this program.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '▾' : '▸'} {title} ({rows.length})
      </button>
      {open && (
        rows.length === 0 ? (
          <div className="muted small" style={{ padding: '4px 8px' }}>none</div>
        ) : (
          <div style={{ overflowX: 'auto', marginTop: 4 }}>
            <table className="dt">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c.key} className="small" style={{ whiteSpace: 'nowrap' }}>
                        {cellVal(r[c.key], !!c.ts) || <span className="muted">-</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

/** The Portal's section titles in the Portal's order, exported so a test can pin the order. */
export const DETAIL_SECTION_ORDER = ['Identity', 'Member', 'Events', 'Member results', 'Kit tracking', 'PCP communication'] as const;

function SectionHead({ title }: { title: string }) {
  return (
    <div className="micro muted" style={{ marginTop: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
      {title}
    </div>
  );
}

export interface MemberDetailPanelProps {
  detail: MemberDetailResp;
  /** Row 418: the member's PCP on the record jumps to the PCP tab (the Portal's "look up this PCP"). */
  onJumpToPcp?: ((key: string) => void) | undefined;
}

export function MemberDetailPanel({ detail, onJumpToPcp }: MemberDetailPanelProps) {
  const m = detail.member ?? {};
  const caps = detail.capabilities;
  const row = (k: string, v: React.ReactNode) =>
    v ? (
      <div className="leader-row" key={k}>
        <span className="k">{k}</span>
        <span className="leader" />
        <span className="v">{v}</span>
      </div>
    ) : null;

  const pcpNpi = s(m.pcp_npi).trim();
  const pcpName = s(m.pcp_name).trim();
  const pcpKey = pcpNpi ? `npi:${pcpNpi}` : pcpName ? `name:${pcpName}` : '';
  const pcpText = [pcpName, pcpNpi ? `NPI ${pcpNpi}` : '', s(m.pcp_phone), s(m.pcp_fax) ? `fax ${s(m.pcp_fax)}` : '']
    .filter(Boolean)
    .join(' · ');
  const dnsEffective = s(m.do_not_solicit).trim();

  return (
    <div>
      <div style={{ fontWeight: 600 }}>
        {s(m.full_name) || 'Member'}{' '}
        <span className="muted">{fmtMemberId(s(m.insurance_id) || s(m.member_id))}</span>
      </div>
      {detail.optOut && (
        <div
          className={detail.optOut.newerEvidence ? 'note warn' : 'note'}
          style={{ marginTop: 8, marginBottom: 4 }}
          aria-label={OPTED_OUT_WORDING.aria(detail.optOut.at)}
        >
          <b>{OPTED_OUT_WORDING.pill}</b>
          {': '}
          {detail.optOut.class}
          {detail.optOut.at ? ` on ${fmtTs(detail.optOut.at)}` : ''}
          {detail.optOut.channel ? ` by ${detail.optOut.channel}` : ''}
          {detail.optOut.source ? ` (${detail.optOut.source})` : ''}
          {detail.optOut.newerEvidence && (
            // HIS ROW 145. The record is immutable and republished on the office's cadence, so a
            // member who asked to come back an hour ago is still opted out in the file. Showing only
            // the file would have the operator tell them they are still blocked, which is a true
            // statement about a FILE presented as a statement about a PERSON.
            <div style={{ marginTop: 4 }}>
              {OPTED_OUT_WORDING.newer(
                fmtTs(detail.optOut.recordPublishedAt),
                fmtTs(detail.optOut.newerEvidence.at),
              )}
              <div className="micro muted" style={{ marginTop: 2 }}>{detail.optOut.newerEvidence.note}</div>
            </div>
          )}
        </div>
      )}
      {detail.cameBack && (
        // HIS 181. History, on its own line, never in the opt-out notice: this member is contactable.
        <div className="micro" style={{ marginTop: 6, marginBottom: 2 }} aria-label="came back">
          <b>Came back</b>: {CAME_BACK_WORDING[detail.cameBack.kind]}
          {detail.cameBack.stoppedAt ? ` - STOP ${fmtTs(detail.cameBack.stoppedAt)}` : ''}
          {detail.cameBack.stoppedAt && detail.cameBack.stopChannel ? ` by ${detail.cameBack.stopChannel}` : ''}
          {detail.cameBack.restartedAt ? `, START ${fmtTs(detail.cameBack.restartedAt)}` : ''}
          {detail.cameBack.restartChannel ? ` by ${detail.cameBack.restartChannel}` : ''}
          . Off the opt-out record.
        </div>
      )}
      {dnsEffective !== '' && (
        // The Portal's DO NOT SOLICIT band: effective date, excluded from all future solicitation.
        <div className="note danger" style={{ marginTop: 8, marginBottom: 4 }}>
          <b>DO NOT SOLICIT</b>
          <span className="small muted" style={{ marginLeft: 8 }}>
            effective {dnsEffective}: exclude from all future solicitation and outreach (mail, SMS, IVR, outbound calls)
          </span>
        </div>
      )}

      <SectionHead title="Identity" />
      <div style={{ marginTop: 4 }}>
        {row('Member ID', s(m.member_id))}
        {row('Insurance ID', s(m.insurance_id))}
        {row('DOB', s(m.dob))}
        {row('Plan', [s(m.lob), s(m.market), s(m.cohort)].filter(Boolean).join(' · '))}
        {row('MSO / IPA', [s(m.mso), s(m.ipa)].filter(Boolean).join(' / '))}
        {row('Kit stage', s(m.kit_stage))}
        {row('Last activity', s(m.last_activity))}
      </div>

      <SectionHead title="Member" />
      <div style={{ marginTop: 4 }}>
        {row('Address', [s(m.address), s(m.city), s(m.state), s(m.zip)].filter(Boolean).join(', '))}
        {row('Phone', [s(m.phone1), s(m.phone2)].filter(Boolean).join(' / '))}
        {row('Email', s(m.email))}
        {row('Language', s(m.language))}
        {row(
          'PCP',
          pcpText
            ? (onJumpToPcp && pcpKey
              ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 0, fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline' }}
                  title={`Look up this PCP (${pcpNpi || pcpName})`}
                  onClick={() => onJumpToPcp(pcpKey)}
                >
                  {pcpText}
                </button>
              )
              : pcpText)
            : '',
        )}
      </div>

      <SectionHead title="Events" />
      <SatelliteSection
        title="Events"
        rows={detail.events}
        cols={[
          { key: 'ts', label: 'When', ts: true },
          { key: 'stage', label: 'Stage' },
          { key: 'event', label: 'Event' },
          { key: 'channel', label: 'Channel' },
          { key: 'outcome', label: 'Outcome' },
        ]}
      />

      <SectionHead title="Member results" />
      <SatelliteSection
        title="Results"
        rows={detail.results}
        notOffered={caps?.results === false}
        cols={[
          { key: 'resulted_ts', label: 'Resulted', ts: true },
          { key: 'kit_barcode', label: 'Kit' },
          { key: 'coarse_result', label: 'Result' },
          { key: 'result_value', label: 'Value' },
          { key: 'status_meaning', label: 'Status' },
        ]}
      />
      <SatelliteSection
        title="Result letters"
        rows={detail.resultLetters}
        notOffered={caps?.resultLetters === false}
        cols={[
          { key: 'sent_date', label: 'Sent', ts: true },
          { key: 'letter_id', label: 'Letter' },
          { key: 'codes', label: 'Codes' },
          { key: 'tracking_code', label: 'Tracking' },
          { key: 'carrier', label: 'Carrier' },
        ]}
      />

      <SectionHead title="Kit tracking" />
      <SatelliteSection
        title="Kits"
        rows={detail.kitTracking}
        cols={[
          { key: 'barcode_id', label: 'Kit' },
          { key: 'ship_date', label: 'Shipped', ts: true },
          { key: 'outbound_status', label: 'Outbound' },
          { key: 'return_status', label: 'Return' },
          { key: 'sample_received_ts', label: 'Sample received', ts: true },
        ]}
      />

      <SectionHead title="PCP communication" />
      <SatelliteSection
        title="Faxes"
        rows={detail.faxTransmissions}
        notOffered={caps?.resultFaxes === false}
        cols={[
          { key: 'ts', label: 'When', ts: true },
          { key: 'direction', label: 'Direction' },
          { key: 'to_number', label: 'To' },
          { key: 'result', label: 'Result' },
          { key: 'pages', label: 'Pages' },
        ]}
      />
    </div>
  );
}
